import {
    createSlice, createAsyncThunk, isRejectedWithValue, isPending, isFulfilled, PayloadAction
} from '@reduxjs/toolkit';
import {
    ApiViewEntityResponse, ApiViewResponseError,
    UserDataModel, UserModel, UserContactDataModel, UserAddressModel,
    UserProfileDataModel, ApiViewResponse, UserValidationDataModel,
    UserProfileResponseModel, UserPasswordModel,
    PaginationResponse, AuditLog, AuditLogDetails, UserPersonalDetailsModel,
    RoleModel, OrganizationMemberModel,
    ApplicationModel,
    SetUserRolesRequest, SetOrganizationUserRolesRequest
} from '../typings'
import { getBodyErrors } from '../services/ApiService';
import identityService from '../services/IdentityService';
import { Message, MessageLevel } from '@lp/lp-ui';

interface UserState {
    loading: boolean,
    loadingAuditLogs: boolean,
    welcomeEmailSent: boolean,
    snack: string | null,
    id: number,
    data: UserModel | null,
    applications: ApplicationModel[],
    roles: RoleModel[],
    organizations: OrganizationMemberModel[],
    auditLogs: PaginationResponse<AuditLog>,
    messages: Message[]
}

const initialState: UserState = {
    loading: false,
    loadingAuditLogs: false,
    welcomeEmailSent: false,
    snack: null,
    id: 0,
    data: null,
    applications: [],
    roles: [],
    organizations: [],
    auditLogs: {
        pageNumber: 0,
        totalCount: 0,
        items: []
    },
    messages: []
}

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        idSelected: (state, action: PayloadAction<number>) => {
            state.id = action.payload;
        },
        userDataSelected: (state, action: PayloadAction<UserDataModel>) => {
            state.data = state.data
                ? { ...state.data, ...action.payload }
                : action.payload as UserModel;
        },
        userDataReset: (state) => {
            state.data = null;
            state.welcomeEmailSent = false;
        },
        snackDisplayed: (state, action: PayloadAction<string | null>) => {
            state.snack = action.payload;
        },
        messagesDisplayed: (state, action: PayloadAction<Message[]>) => {
            state.messages = action.payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(validateUserProfile.fulfilled, (state) => {
                state.loading = false;
                state.messages = [];
                state.snack = null;
            })
            .addCase(createUserApplicationRelation.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.snack = null;
                if (state.data !== null) {
                    const request = action.meta.arg;
                    const applicationsClone = Object.assign([], state.applications);
                    applicationsClone.push(request.application);
                    state.applications = applicationsClone;
                }
            })
            .addCase(deleteUserApplicationRelation.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.snack = null;
                if (state.data !== null) {
                    const request = action.meta.arg;
                    const index = state.applications.findIndex(c => c.id === request.application.id);
                    if (index !== undefined && index > -1) {
                        const applicationsClone = Object.assign([], state.applications);
                        applicationsClone.splice(index, 1);
                        state.applications = applicationsClone;
                    }
                }
            })
            .addCase(createUserRoleRelation.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.snack = null;
                if (state.data !== null) {
                    const request = action.meta.arg;
                    const rolesClone = Object.assign([], state.roles);
                    rolesClone.push(request.role);
                    state.roles = rolesClone;
                }
            })
            .addCase(deleteUserRoleRelation.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.snack = null;
                if (state.data !== null) {
                    const request = action.meta.arg;
                    const index = state.roles.findIndex(c => c.id === request.role.id);
                    if (index !== undefined && index > -1) {
                        const rolesClone = Object.assign([], state.roles);
                        rolesClone.splice(index, 1);
                        state.roles = rolesClone;
                    }
                }
            })
            .addCase(setUserRoles.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = [];
                // A staged save batches many toggles behind one click, so it needs an explicit
                // acknowledgement. Kept out of isApiFulfilledWithSnackMessage because that
                // matcher also assigns payload.data to state.data, a RoleModel[] here.
                state.snack = "Record Saved.";
                state.roles = action.payload.data ?? [];
            })
            .addCase(setOrganizationUserRoles.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.snack = "Record Saved.";
                const { organizationId } = action.meta.arg;
                const orgIndex = state.organizations.findIndex(o => o.organizationId === organizationId);
                if (orgIndex > -1) {
                    const organizationsClone: OrganizationMemberModel[] = Object.assign([], state.organizations);
                    organizationsClone[orgIndex] = {
                        ...organizationsClone[orgIndex],
                        roles: action.payload.data ?? []
                    };
                    state.organizations = organizationsClone;
                }
            })
            .addCase(addUserToOrganization.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.snack = null;
                if (state.data !== null) {
                    const request = action.meta.arg;
                    const index = state.organizations.findIndex(c => c.organizationId === request.organizationId);
                    const organizationsClone: OrganizationMemberModel[] = Object.assign([], state.organizations);
                    if (index !== undefined && index > -1) {
                        organizationsClone[index] = request;
                    } else {
                        organizationsClone.push(request);
                    }
                    state.organizations = organizationsClone;
                }
            })
            .addCase(removeUserFromOrganization.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.snack = null;
                if (state.data !== null) {
                    const request = action.meta.arg;
                    const index = state.organizations.findIndex(c => c.organizationId === request.organizationId);
                    if (index !== undefined && index > -1) {
                        const organizationsClone = Object.assign([], state.organizations);
                        organizationsClone.splice(index, 1);
                        state.organizations = organizationsClone;
                    }
                }
            })
            .addCase(emailUserPassword.fulfilled, (state) => {
                state.loading = false;
                state.messages = [{ details: "Email has been successfully sent.", level: MessageLevel.Information }];
                state.snack = null;
            })
            .addCase(emailNewUser.fulfilled, (state) => {
                state.loading = false;
                state.messages = [];
                state.welcomeEmailSent = true;
                state.snack = null;
            })
            .addCase(sendWelcomeEmail.fulfilled, (state) => {
                state.loading = false;
                state.messages = [];
                state.welcomeEmailSent = true;
                state.snack = null;
            })
            .addCase(getAuditLogs.pending, (state) => {
                state.loadingAuditLogs = true;
            })
            .addCase(getAuditLogs.rejected, (state, action) => {
                state.loadingAuditLogs = false;
                state.snack = null;
                if (action.payload) {
                    state.messages = action.payload.map(e => ({ details: e.detail, level: MessageLevel.Error }));
                }
            })
            .addCase(getAuditLogs.fulfilled, (state, action) => {
                state.loadingAuditLogs = false;
                state.snack = null;
                state.auditLogs = action.payload.data;
            })
            .addCase(getAuditLogDetails.fulfilled, (state) => {
                state.loading = false;
                state.snack = null;
            })
            .addCase(getUserRoles.fulfilled, (state, action) => {
                state.loading = false;
                state.snack = null;
                state.roles = action.payload.data;
            })
            .addCase(getUserApplications.fulfilled, (state, action) => {
                state.loading = false;
                state.snack = null;
                state.applications = action.payload.data;
            })
            .addCase(getUserOrganizations.fulfilled, (state, action) => {
                state.loading = false;
                state.snack = null;
                state.organizations = action.payload.data;
            })
            .addMatcher(isApiFulfilledWithoutSnack, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.snack = null;
                state.data = action.payload.data;
            })
            .addMatcher(isApiFulfilledWithSnackMessage, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.snack = "Record Saved.";
                state.data = action.payload.data;
            })
            .addMatcher(isApiPending, (state) => {
                state.loading = true;
            })
            .addMatcher(isApiRejected, (state, action) => {
                state.loading = false;
                state.snack = null;
                if (action.payload) {
                    state.messages = action.payload.map(e => ({ details: e.detail, level: MessageLevel.Error }));
                }
            })
    }
});

export const getUser = createAsyncThunk<ApiViewEntityResponse<UserModel>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/get', async (id: number, { rejectWithValue }) => {
        try {
            const response = await identityService.getUser(id);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const validateUserProfile = createAsyncThunk<ApiViewResponse, UserValidationDataModel, { rejectValue: ApiViewResponseError[] }>
    ('user/profile/validate', async (data: UserValidationDataModel, { rejectWithValue }) => {
        try {
            const response = await identityService.validateUserProfile(data);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const createUserProfile = createAsyncThunk<ApiViewEntityResponse<UserProfileResponseModel>, UserDataModel, { rejectValue: ApiViewResponseError[] }>
    ('user/profile/create', async (profile: UserDataModel, { rejectWithValue }) => {
        try {
            const response = await identityService.createUserProfile(profile);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateUserProfile = createAsyncThunk<ApiViewEntityResponse<UserModel>, UpdateUserProfileRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/profile/update', async (request: UpdateUserProfileRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.updateUserProfile(request.userId, request.profile);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateUserPersonalDetails = createAsyncThunk<ApiViewEntityResponse<UserModel>, UpdateUserPersonalDetailsRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/personal-details/update', async (request: UpdateUserPersonalDetailsRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.updateUserPersonalDetails(request.userId, request.personalDetails);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const createUserAddress = createAsyncThunk<ApiViewEntityResponse<UserModel>, CreateUserAddressRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/address/create', async (request: CreateUserAddressRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.createUserAddress(request.userId, request.userAddress);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateUserAddress = createAsyncThunk<ApiViewEntityResponse<UserModel>, UpdateUserAddressRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/address/update', async (request: UpdateUserAddressRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.updateUserAddress(request.userId, request.userAddressId, request.userAddress);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const createUserContact = createAsyncThunk<ApiViewEntityResponse<UserModel>, CreateUserContactRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/contact/create', async (request: CreateUserContactRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.createUserContact(request.userId, request.contact);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateUserContact = createAsyncThunk<ApiViewEntityResponse<UserModel>, UpdateUserContactRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/contact/update', async (request: UpdateUserContactRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.updateUserContact(request.userId, request.contactId, request.contact);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const deleteUserContact = createAsyncThunk<ApiViewEntityResponse<UserModel>, DeleteUserContactRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/contact/delete', async (request: DeleteUserContactRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.deleteUserContact(request.userId, request.contactId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const getUserApplications = createAsyncThunk<ApiViewEntityResponse<ApplicationModel[]>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/applications/get', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.getUserApplications(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const createUserApplicationRelation = createAsyncThunk<ApiViewResponse, UserApplicationRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/application/create', async (request: UserApplicationRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.createUserApplicationRelation(request.userId, request.application.id);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const deleteUserApplicationRelation = createAsyncThunk<ApiViewResponse, UserApplicationRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/application/delete', async (request: UserApplicationRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.deleteUserApplicationRelation(request.userId, request.application.id);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const enableUser = createAsyncThunk<ApiViewEntityResponse<UserModel>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/enable', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.enableUser(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const disableUser = createAsyncThunk<ApiViewEntityResponse<UserModel>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/disable', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.disableUser(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const enableMfa = createAsyncThunk<ApiViewEntityResponse<UserModel>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/mfa/enable', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.enableMfa(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const disableMfa = createAsyncThunk<ApiViewEntityResponse<UserModel>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/mfa/disable', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.disableMfa(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const forcePasswordReset = createAsyncThunk<ApiViewEntityResponse<UserProfileResponseModel>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/forcePasswordReset', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.forcePasswordReset(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const sendWelcomeEmail = createAsyncThunk<ApiViewEntityResponse<UserProfileResponseModel>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/sendWelcomeEmail', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.sendWelcomeEmail(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const emailUserPassword = createAsyncThunk<ApiViewEntityResponse<UserModel>, UserPasswordModel, { rejectValue: ApiViewResponseError[] }>
    ('user/emailUserPassword', async (request: UserPasswordModel, { rejectWithValue }) => {
        try {
            const response = await identityService.emailUserPassword(request.id, request.password);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const emailNewUser = createAsyncThunk<ApiViewEntityResponse<UserModel>, UserPasswordModel, { rejectValue: ApiViewResponseError[] }>
    ('user/emailNewUser', async (request: UserPasswordModel, { rejectWithValue }) => {
        try {
            const response = await identityService.emailNewUser(request.id, request.password);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateUserName = createAsyncThunk<ApiViewEntityResponse<UserModel>, UpdateUserNameRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/update/userName', async (request: UpdateUserNameRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.updateUserName(request.userId, request.userName);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateEmail = createAsyncThunk<ApiViewEntityResponse<UserModel>, UpdateEmailRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/update/email', async (request: UpdateEmailRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.updateEmail(request.userId, request.email);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const getAuditLogs = createAsyncThunk<ApiViewEntityResponse<PaginationResponse<AuditLog>>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/auditLogs/get', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.getAuditLogs(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const getAuditLogDetails = createAsyncThunk<ApiViewEntityResponse<AuditLogDetails>, GetAuditLogDetailsRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/auditLogs/details/get', async (request: GetAuditLogDetailsRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.getAuditLogDetails(request.userId, request.auditLogId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const getUserRoles = createAsyncThunk<ApiViewEntityResponse<RoleModel[]>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/roles/get', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.getUserRoles(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const createUserRoleRelation = createAsyncThunk<ApiViewResponse, UserRoleRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/role/create', async (request: UserRoleRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.createUserRoleRelation(request.userId, request.role.id);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const deleteUserRoleRelation = createAsyncThunk<ApiViewResponse, UserRoleRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/role/delete', async (request: UserRoleRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.deleteUserRoleRelation(request.userId, request.role.id);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const setUserRoles = createAsyncThunk<ApiViewEntityResponse<RoleModel[]>, SetUserRolesRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/roles/set', async (request: SetUserRolesRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.setUserRoles(request.userId, request.roleIds);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const setOrganizationUserRoles = createAsyncThunk<ApiViewEntityResponse<RoleModel[]>, SetOrganizationUserRolesRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/organization/roles/set', async (request: SetOrganizationUserRolesRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.setOrganizationUserRoles(request.userId, request.organizationId, request.roleIds);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const sendUserToQlik = createAsyncThunk<ApiViewEntityResponse<UserModel>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/sendToQlik', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.sendUserToQlik(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const getUserOrganizations = createAsyncThunk<ApiViewEntityResponse<OrganizationMemberModel[]>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/organizations/get', async (userId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.getUserOrganizations(userId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const addUserToOrganization = createAsyncThunk<ApiViewResponse, OrganizationMemberRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/organization/add', async (request: OrganizationMemberRequest, { rejectWithValue }) => {
        try {
            const roles = request.roles.map(r => r.id);
            const response = await identityService.addUserToOrganization(request.userId, request.organizationId, roles);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const removeUserFromOrganization = createAsyncThunk<ApiViewResponse, OrganizationUserRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/organization/remove', async (request: OrganizationUserRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.removeUserFromOrganization(request.userId, request.organizationId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

const isApiPending = isPending(
    getUser, validateUserProfile, createUserProfile, updateUserProfile, updateUserPersonalDetails,
    createUserAddress, updateUserAddress, createUserContact, updateUserContact, deleteUserContact,
    getUserApplications, createUserApplicationRelation, deleteUserApplicationRelation, enableUser, disableUser, enableMfa, disableMfa,
    forcePasswordReset, sendWelcomeEmail, emailUserPassword, emailNewUser, updateUserName, updateEmail, getAuditLogDetails,
    getUserRoles, createUserRoleRelation, deleteUserRoleRelation, setUserRoles, sendUserToQlik,
    getUserOrganizations, addUserToOrganization, removeUserFromOrganization,
    setOrganizationUserRoles);
const isApiRejected = isRejectedWithValue(
    getUser, validateUserProfile, createUserProfile, updateUserProfile, updateUserPersonalDetails,
    createUserAddress, updateUserAddress, createUserContact, updateUserContact, deleteUserContact,
    getUserApplications,createUserApplicationRelation, deleteUserApplicationRelation, enableUser, disableUser, enableMfa, disableMfa,
    forcePasswordReset, sendWelcomeEmail, emailUserPassword, emailNewUser, updateUserName, updateEmail, getAuditLogs,
    getAuditLogDetails, getUserRoles, createUserRoleRelation, deleteUserRoleRelation, setUserRoles, sendUserToQlik,
    getUserOrganizations, addUserToOrganization, removeUserFromOrganization,
    setOrganizationUserRoles);
const isApiFulfilledWithSnackMessage = isFulfilled(
    updateUserProfile, updateUserPersonalDetails, createUserAddress,
    updateUserAddress, createUserContact, updateUserContact,
    deleteUserContact, sendUserToQlik);
const isApiFulfilledWithoutSnack = isFulfilled(
    getUser, createUserProfile, enableUser, disableUser, enableMfa, disableMfa,
    forcePasswordReset, updateUserName, updateEmail);

export default userSlice.reducer

export const {
    idSelected,
    userDataSelected,
    userDataReset,
    snackDisplayed,
    messagesDisplayed
} = userSlice.actions;

export interface UpdateUserProfileRequest {
    userId: number,
    profile: UserProfileDataModel
}

export interface UpdateUserPersonalDetailsRequest {
    userId: number,
    personalDetails: UserPersonalDetailsModel
}

export interface CreateUserAddressRequest {
    userId: number,
    userAddress: UserAddressModel
}

export interface UpdateUserAddressRequest extends CreateUserAddressRequest {
    userAddressId: number
}

export interface CreateUserContactRequest {
    userId: number,
    contact: UserContactDataModel
}

export interface UpdateUserContactRequest extends CreateUserContactRequest {
    contactId: number
}

export interface DeleteUserContactRequest {
    userId: number,
    contactId: number
}

export interface UserApplicationRequest {
    userId: number,
    application: ApplicationModel
}

export interface UserRoleRequest {
    userId: number,
    role: RoleModel
}

export interface UpdateUserNameRequest {
    userId: number,
    userName: string
}

export interface UpdateEmailRequest {
    userId: number,
    email: string
}

export interface GetAuditLogDetailsRequest {
    userId: number,
    auditLogId: number
}

export interface OrganizationUserRequest {
    userId: number,
    organizationId: number
}

export interface OrganizationMemberRequest extends OrganizationMemberModel {
    userId: number
}
